import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tabs,
    Tab,
    Box,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import { Formik, Form } from 'formik';
import { uwinAPI } from '../../../../services/uwin.api';
import DeliveryForm from './DeliveryForm';
import AbortionForm from './AbortionForm';
import MaternalDeathForm from './MaternalDeathForm';
import {
    getDeliveryValidationSchema,
    getAbortionValidationSchema,
    getMaternalDeathValidationSchema,
    deliveryInitialValues,
    abortionInitialValues,
    maternalDeathInitialValues
} from './validationSchemas';

const PregnancyOutCome = (props) => {
    const { 
        isModalOpen, 
        isModalClose, 
        pregnancyDetails, 
        latestVaccinationDate, 
        getPregnancyData, 
        setIsNewbornRegistrationActive, 
        setPregnancyOutcomeData,
        benProfileData
    } = props;
    const [outcomeType, setOutcomeType] = useState('Delivery');
    const [maternalDeathMinDate, setMaternalDeathMinDate] = useState(null);

    // Calculate minDate for abortion: max of latestVaccinationDate and lmc_date
    const getAbortionMinDate = () => {
        const lmcDate = pregnancyDetails?.lmc_date;
        const vaccinationDate = latestVaccinationDate;

        // If both are empty/null, return null
        if (!lmcDate && !vaccinationDate) {
            return null;
        }

        // If only one exists, return that one
        if (!lmcDate) return vaccinationDate;
        if (!vaccinationDate) return lmcDate;

        // Both exist, return the later date
        const lmcDateObj = new Date(lmcDate);
        const vaccinationDateObj = new Date(vaccinationDate);

        return lmcDateObj > vaccinationDateObj ? lmcDate : vaccinationDate;
    };

    // Calculate minDate for delivery: lmc_date + 140 days (20 weeks)
    const getDeliveryMinDate = () => {
        const lmcDate = pregnancyDetails?.lmc_date;

        if (!lmcDate) {
            return null;
        }

        const lmcDateObj = new Date(lmcDate);
        // Add 140 days (20 weeks)
        lmcDateObj.setDate(lmcDateObj.getDate() + 140);

        // Format as YYYY-MM-DD
        const year = lmcDateObj.getFullYear();
        const month = String(lmcDateObj.getMonth() + 1).padStart(2, '0');
        const day = String(lmcDateObj.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };


    // Master data states
    const [masterData, setMasterData] = useState({
        placeOfDeliveryOptions: [],
        typeOfDeliveryOptions: [],
        deliveryComplicationsOptions: [],
        conductedDeliveryOptions: [],
        abortionTypeOptions: []
    });

    // Get initial values based on outcome type
    const getInitialValues = () => {
        switch (outcomeType) {
            case 'Delivery':
                return deliveryInitialValues;
            case 'Abortion':
                return abortionInitialValues;
            case 'Maternal Death':
                return maternalDeathInitialValues;
            default:
                return deliveryInitialValues;
        }
    };

    // Get validation schema based on outcome type
    const getValidationSchema = () => {
        const today = new Date().toISOString().split('T')[0];

        switch (outcomeType) {
            case 'Delivery':
                return getDeliveryValidationSchema(getDeliveryMinDate(), today);
            case 'Abortion':
                return getAbortionValidationSchema(getAbortionMinDate(), today);
            case 'Maternal Death':
                return getMaternalDeathValidationSchema(maternalDeathMinDate, today);
            default:
                return getDeliveryValidationSchema(getDeliveryMinDate(), today);
        }
    };

    const handleTabChange = (event, newValue) => {
        setOutcomeType(newValue);
    };

    useEffect(() => {
        fetchPregnancyMaster();
        fetchMinDateForDeathAndAbortion();
    }, []);

    const fetchPregnancyMaster = async () => {
        try {
            const response = await uwinAPI.getPregnancyMaster();

            if (response?.success) {
                const data = response?.data;

                setMasterData({
                    placeOfDeliveryOptions: data?.place_of_delivery || [],
                    typeOfDeliveryOptions: data?.type_of_delivery || [],
                    deliveryComplicationsOptions: data?.delivery_complications || [],
                    conductedDeliveryOptions: data?.who_conducted_delivery || [],
                    abortionTypeOptions: data?.abortion_type || []
                });
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to fetch pregnancy master data');
        }
    };

    const fetchMinDateForDeathAndAbortion = async () => {
        try {
            const datePayload = { beneficiary_ref_id: benProfileData?.beneficiary_reference_id }

            const response = await uwinAPI.minDateForDeathAndAbortion(datePayload);

            if (response?.success) {
                setMaternalDeathMinDate(response?.data?.minDateForDeclareDeath)
            }
        } catch (error) {
            toast.error(error?.response?.data?.message);
        }
    };

    // Build payload based on outcome type
    const buildPayload = (values, type) => {
        // Common payload fields from pregnancyDetails
        const commonPayload = {
            beneficiary_id: pregnancyDetails?.beneficiary_id,
            pregnancy_number: pregnancyDetails?.pregnancy_number,
            session_site_id: pregnancyDetails?.session_site_id,
            type: type
        };

        if (type === 'Delivery') {
            const deliveryPayload = {
                ...commonPayload,
                place_of_delivery: values?.place_of_delivery,
                type_of_delivery: values?.type_of_delivery,
                date_of_delivery: values?.date_of_delivery,
            };

            // Add conditional fields based on place_of_delivery
            if (values?.place_of_delivery === 'Home') {
                deliveryPayload.who_conducted_delivery = values?.who_conducted_delivery;

                // Check if array contains 'Other' or acts like it
                const isOther = Array.isArray(values?.who_conducted_delivery)
                    ? values.who_conducted_delivery.includes('Other')
                    : values?.who_conducted_delivery === 'Other';

                if (isOther) {
                    deliveryPayload.who_conducted_delivery_other = values?.who_conducted_delivery_other;
                }
            } else if (values?.place_of_delivery === 'Institutional') {
                // Filter out "None" if other complications are selected
                const complications = values?.delivery_complications.filter(item => item !== 'None');
                deliveryPayload.delivery_complications = complications.length > 0 ? complications : values?.delivery_complications;

                if (values?.delivery_complications.includes('Other')) {
                    deliveryPayload.delivery_complication_other = values?.delivery_complication_other;
                } else {
                    deliveryPayload.delivery_complication_other = '';
                }
            }

            return deliveryPayload;
        } else if (type === 'Abortion') {
            return {
                ...commonPayload,
                date_of_abortion: values?.date_of_abortion,
                abortion_type: values?.abortion_type,
            };
        } else if (type === 'Maternal Death') {
            return {
                ...commonPayload,
                date_of_death: values?.date_of_death,
            };
        }

        return commonPayload;
    };

    // Handle form submission
    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const payload = buildPayload(values, outcomeType);

            const response = await uwinAPI.savePregnancyOutcome(payload);

            if (response?.success) {
                const responseData = response?.data?.data;

                const successMessage = response?.message || `${outcomeType} outcome saved successfully`;

                toast.success(successMessage);
                resetForm();

                if (responseData?.type === "Delivery") {
                    setIsNewbornRegistrationActive?.(true);
                    setPregnancyOutcomeData?.(responseData);
                }

                getPregnancyData?.();
                isModalClose?.();
            }
        } catch (error) {
            toast.error(error?.response?.data?.data?.message || 'An error occurred while saving');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={isModalOpen}
            onClose={isModalClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: "16px" } }}
        >
            <DialogTitle>
                Pregnancy Outcome
                <IconButton
                    onClick={isModalClose}
                    sx={{ position: "absolute", right: 16, top: 16 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 4, pb: 4 }} dividers>
                {/* Tabs */}
                <Tabs
                    value={outcomeType}
                    onChange={handleTabChange}
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{
                        mb: 3,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Tab label="Delivery" value="Delivery" />
                    <Tab label="Abortion" value="Abortion" />
                    <Tab label="Maternal Death" value="Maternal Death" />
                </Tabs>

                {/* Formik Wrapper */}
                <Formik
                    key={outcomeType} // Reset form when tab changes
                    initialValues={getInitialValues()}
                    validationSchema={getValidationSchema()}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                        <Form>
                            <Box>
                                {outcomeType === 'Delivery' && (
                                    <DeliveryForm
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        setFieldValue={setFieldValue}
                                        isSubmitting={isSubmitting}
                                        masterData={masterData}
                                        minDate={getDeliveryMinDate()}
                                    />
                                )}

                                {outcomeType === 'Abortion' && (
                                    <AbortionForm
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        setFieldValue={setFieldValue}
                                        isSubmitting={isSubmitting}
                                        masterData={masterData}
                                        minDate={getAbortionMinDate()}
                                    />
                                )}

                                {outcomeType === 'Maternal Death' && (
                                    <MaternalDeathForm
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        setFieldValue={setFieldValue}
                                        isSubmitting={isSubmitting}
                                        maternalDeathMinDate={maternalDeathMinDate}
                                    />
                                )}
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default PregnancyOutCome;
